import { create } from 'zustand';
import type { Difference } from 'store/request/differences/types';
import type { DifferenceNumbering, SubgroupedDifferences } from '@verify/shared-utils';
import type { SortDifferencesOption } from 'types';
import type { InspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { computeNumberedModuleDifferences } from 'store/request/inspections/selectors';

export type NumberedDifference = Difference & DifferenceNumbering;

/** Shallow-equal over own keys; nested objects compared by reference. */
export function shallowEqualCard(a: NumberedDifference, b: NumberedDifference): boolean {
  if (a === b) return true;
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  for (const k of ak) {
    const aVal = (a as unknown as Record<string, unknown>)[k];
    const bVal = (b as unknown as Record<string, unknown>)[k];
    if (aVal !== bVal) return false;
  }
  return true;
}

/**
 * Reuse prior card wrappers when a rebuild produces shallow-equal content so
 * React.memo on DifferenceElement can skip unchanged siblings.
 */
export function reconcileCards(prev: NumberedDifference[], next: NumberedDifference[]): NumberedDifference[] {
  if (prev.length === 0) return next;
  const prevById = new Map(prev.map((c) => [c.id, c]));
  const reconciled = next.map((card) => {
    const old = prevById.get(card.id);
    return old && shallowEqualCard(old, card) ? old : card;
  });
  if (reconciled.length === prev.length && reconciled.every((c, i) => c === prev[i])) {
    return prev;
  }
  return reconciled;
}

/**
 * Buckets numbered differences into their groups, keyed by every grouped member's id.
 * Only groups with 2+ members are kept (ungrouped diffs need no on-document label or buttons).
 * Members within each bucket are ordered by subgroupIndex (reading order), so the draw code can
 * read the N.k label and pick the prev/next popup targets straight from this list.
 */
export function buildGroupMembersByDifferenceId(numbered: NumberedDifference[]): Record<string, NumberedDifference[]> {
  const byGroupIndex = new Map<number, NumberedDifference[]>();
  for (const diff of numbered) {
    const bucket = byGroupIndex.get(diff.groupIndex) ?? [];
    bucket.push(diff);
    byGroupIndex.set(diff.groupIndex, bucket);
  }

  const byDifferenceId: Record<string, NumberedDifference[]> = {};
  for (const bucket of byGroupIndex.values()) {
    if (bucket.length <= 1) continue;
    bucket.sort((a, b) => (a.subgroupIndex ?? 0) - (b.subgroupIndex ?? 0));
    for (const member of bucket) {
      byDifferenceId[member.id] = bucket;
    }
  }
  return byDifferenceId;
}

interface DifferenceListStore {
  /** Sorted, filtered difference cards to render. 1 'representative card' for grouped differences. ListOfDifferences reads this and renders — no extra sort or filter needed. Empty on the All tab and before the first computation. */
  differenceCards: NumberedDifference[];
  /** Grouped members (2+) keyed by every member's id → the group's members in reading order. The on-document draw code reads this to build the N.k label and the prev/next popup buttons. Empty on the All tab and before the first computation. */
  groupMembersByDifferenceId: Record<string, NumberedDifference[]>;
  setDifferenceList: (
    differenceCards: NumberedDifference[],
    groupMembersByDifferenceId: Record<string, NumberedDifference[]>,
  ) => void;
  clear: () => void;
}

export const useDifferenceListStore = create<DifferenceListStore>((set) => ({
  differenceCards: [],
  groupMembersByDifferenceId: {},
  setDifferenceList: (differenceCards, groupMembersByDifferenceId) =>
    set((state) => ({
      differenceCards: reconcileCards(state.differenceCards, differenceCards),
      groupMembersByDifferenceId,
    })),
  clear: () => set({ differenceCards: [], groupMembersByDifferenceId: {} }),
}));

/**
 * Computes the current module's numbered card list and writes it to the store.
 *
 * Shared by two triggers so their logic can't drift:
 * - the recomputeDifferenceNumbering saga (fires on difference-data / sort / module changes), and
 * - ListOfDifferences (fires when custom status titles arrive from React Query — the saga's Redux
 *   data-watcher can't observe that, since titles never flow through Redux).
 *
 * On the All tab the store is cleared: that tab uses the legacy sort/number path in ListOfDifferences.
 * Custom status titles are extracted here (not by callers) so the mapping lives in one place.
 */
export function rebuildDifferenceList(
  subgrouped: SubgroupedDifferences<Difference>,
  unfilteredDifferences: Difference[],
  selectedModule: string,
  sortOption: SortDifferencesOption,
  statuses: InspectionCustomStatuses | null | undefined,
): void {
  if (selectedModule === 'All') {
    useDifferenceListStore.getState().clear();
    return;
  }

  const statusTitlesById: Record<string, string> = {};
  if (statuses?.allStatuses) {
    for (const [id, status] of Object.entries(statuses.allStatuses)) {
      statusTitlesById[id] = status.title;
    }
  }

  const numbered = computeNumberedModuleDifferences(
    subgrouped,
    unfilteredDifferences,
    selectedModule,
    sortOption,
    statusTitlesById,
  ) as NumberedDifference[];

  const differenceCards = numbered.filter((d) => d.subgroupIndex === 1 || d.subgroupIndex === undefined);
  const groupMembersByDifferenceId = buildGroupMembersByDifferenceId(numbered);

  useDifferenceListStore.getState().setDifferenceList(differenceCards, groupMembersByDifferenceId);
}
